import { Order, CreateOrderRequest, UpdateOrderRequest } from '../../core/domain/entities/Order';
import { User, LoginRequest, LoginResponse } from '../../core/domain/entities/User';
import { Customer, CreateCustomerRequest, UpdateCustomerRequest } from '../../core/domain/entities/Customer';
import { Product, CreateProductRequest, UpdateProductRequest } from '../../core/domain/entities/Product';
import { Warehouse, CreateWarehouseRequest, UpdateWarehouseRequest } from '../../core/domain/entities/Warehouse';
import { Analytics } from '../../core/domain/entities/Analytics';
import { Settings, UpdateSettingsRequest } from '../../core/domain/entities/Settings';
import { Notification, CreateNotificationRequest, UpdateNotificationRequest } from '../../core/domain/entities/Notification';

// API Response Interface
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Cache for API responses
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Debounce function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

export class ApiService {
  public baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://orderapp-backend.ssemrah33.workers.dev/api';
  }

  // Get authentication token from localStorage
  public getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Set authentication token to localStorage
  setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  // Remove authentication token from localStorage
  removeAuthToken(): void {
    localStorage.removeItem('authToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      
      // Get auth token
      const token = this.getAuthToken();
      
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers as Record<string, string>,
      };

      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Check cache first
      const cacheKey = `${options.method || 'GET'}_${url}`;
      const cached = cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return { success: true, data: cached.data };
      }

      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (response.status === 401) {
        // Token expired or invalid, remove it
        this.removeAuthToken();
        return {
          success: false,
          error: 'Authentication failed. Please login again.'
        };
      }

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        return {
          success: false,
          error: `Rate limit exceeded. Please try again in ${retryAfter || 60} seconds.`
        };
      }

      if (!response.ok) {
        console.log('❌ HTTP Error Response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
        
        const errorData = await response.json().catch(() => ({}));
        console.log('❌ Error Response Data:', errorData);
        
        return {
          success: false,
          error: errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const responseData = await response.json();
      
      // Handle different response formats
      let data = responseData;
      
      // If response has a data property, use it
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        data = responseData.data;
      }
      
      // Ensure arrays are properly handled
      if (Array.isArray(data)) {
        // Data is already an array, use as is
      } else if (data && typeof data === 'object' && data.items && Array.isArray(data.items)) {
        // Some APIs return { items: [...] } format
        data = data.items;
      } else if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        // Empty response, return empty array for array endpoints
        const isArrayEndpoint = endpoint.includes('/orders') || 
                               endpoint.includes('/customers') || 
                               endpoint.includes('/products') || 
                               endpoint.includes('/notifications');
        if (isArrayEndpoint) {
          data = [];
        }
      }
      
      // Cache successful responses
      if (data && options.method !== 'POST' && options.method !== 'PUT' && options.method !== 'DELETE') {
        cache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return { success: true, data };
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection.'
      };
    }
  }

  // Debounced methods to prevent rapid successive calls
  private debouncedRequest = debounce(this.request.bind(this), 300);

  // Order endpoints
  async getOrders(): Promise<ApiResponse<Order[]>> {
    return this.request<Order[]>('/orders');
  }

  async getOrder(id: string): Promise<ApiResponse<Order>> {
    console.log("🔍 ApiService.getOrder called with ID:", id);
    console.log("🔍 Full URL will be:", `${this.baseUrl}/orders/${id}`);
    
    try {
      const response = await fetch(`${this.baseUrl}/orders/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const responseData = await response.json();
      console.log("🔍 ApiService.getOrder raw response:", responseData);
      
      // Backend returns { data: Order, success: boolean }
      if (responseData.data) {
        console.log("🔍 ApiService.getOrder returning order:", responseData.data);
        return {
          success: true,
          data: responseData.data
        };
      } else if (responseData.order) {
        // Fallback for old format
        console.log("🔍 ApiService.getOrder returning order (fallback):", responseData.order);
        return {
          success: true,
          data: responseData.order
        };
      } else {
        console.log("🔍 ApiService.getOrder no order found in response");
        return {
          success: false,
          error: 'Order not found in response'
        };
      }
    } catch (error) {
      console.error('ApiService.getOrder error:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection.'
      };
    }
  }

  async createOrder(orderData: CreateOrderRequest): Promise<ApiResponse<Order>> {
    console.log('🔍 createOrder called with:', orderData);
    
    const response = await fetch(`${this.baseUrl}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    console.log('🔍 Full API response:', responseData);
    console.log('🔍 Response data:', responseData.data);
    
    // Backend'den gelen tam response'u döndür
    return {
      success: responseData.success,
      data: responseData.data,
      message: responseData.message
    };
  }

  async updateOrder(orderId: string, orderData: UpdateOrderRequest): Promise<ApiResponse<Order>> {
    return this.request<Order>(`/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });
  }

  async updateOrderStatus(orderId: string, status: string, notes?: string): Promise<ApiResponse<Order>> {
    console.log('ApiService.updateOrderStatus called with:', { orderId, status, notes });
    
    try {
      const response = await fetch(`${this.baseUrl}/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ status, notes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const responseData = await response.json();
      console.log('ApiService.updateOrderStatus raw response:', responseData);
      
      // Backend returns { data: Order, success: boolean } or { order: Order, message: string }
      if (responseData.data) {
        console.log('ApiService.updateOrderStatus returning order:', responseData.data);
        return {
          success: true,
          data: responseData.data
        };
      } else if (responseData.order) {
        // Fallback for old format
        console.log('ApiService.updateOrderStatus returning order (fallback):', responseData.order);
        return {
          success: true,
          data: responseData.order
        };
      } else {
        console.log('ApiService.updateOrderStatus no order found in response');
        return {
          success: false,
          error: 'Order not found in response'
        };
      }
    } catch (error) {
      console.error('ApiService.updateOrderStatus error:', error);
      return {
        success: false,
        error: 'Network error. Please check your connection.'
      };
    }
  }

  async deleteOrder(orderId: string): Promise<ApiResponse<void>> {
    console.log('🗑️ ApiService.deleteOrder called with ID:', orderId);
    const response = await this.request<void>(`/orders/${orderId}`, {
      method: 'DELETE',
    });
    console.log('🗑️ ApiService.deleteOrder response:', response);
    return response;
  }

  // Customer endpoints
  async getCustomers(): Promise<ApiResponse<Customer[]>> {
    return this.request<Customer[]>('/customers');
  }

  async getCustomer(id: string): Promise<ApiResponse<Customer>> {
    return this.request<Customer>(`/customers/${id}`);
  }

  async createCustomer(customerData: CreateCustomerRequest): Promise<ApiResponse<Customer>> {
    return this.request<Customer>('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomer(customerId: string, customerData: UpdateCustomerRequest): Promise<ApiResponse<Customer>> {
    return this.request<Customer>(`/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async deleteCustomer(customerId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/customers/${customerId}`, {
      method: 'DELETE',
    });
  }

  // Product endpoints
  async getProducts(): Promise<ApiResponse<Product[]>> {
    return this.request<Product[]>('/products');
  }

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/products/${id}`);
  }

  async createProduct(productData: CreateProductRequest): Promise<ApiResponse<Product>> {
    return this.request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(productId: string, productData: UpdateProductRequest): Promise<ApiResponse<Product>> {
    return this.request<Product>(`/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(productId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/products/${productId}`, {
      method: 'DELETE',
    });
  }

  // Warehouse endpoints
  async getWarehouses(): Promise<ApiResponse<Warehouse[]>> {
    return this.request<Warehouse[]>('/warehouses');
  }

  async getWarehouse(id: string): Promise<ApiResponse<Warehouse>> {
    return this.request<Warehouse>(`/warehouses/${id}`);
  }

  async createWarehouse(warehouseData: CreateWarehouseRequest): Promise<ApiResponse<Warehouse>> {
    return this.request<Warehouse>('/warehouses', {
      method: 'POST',
      body: JSON.stringify(warehouseData),
    });
  }

  async updateWarehouse(warehouseId: string, warehouseData: UpdateWarehouseRequest): Promise<ApiResponse<Warehouse>> {
    return this.request<Warehouse>(`/warehouses/${warehouseId}`, {
      method: 'PUT',
      body: JSON.stringify(warehouseData),
    });
  }

  async deleteWarehouse(warehouseId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/warehouses/${warehouseId}`, {
      method: 'DELETE',
    });
  }

  // User endpoints
  async getCurrentUser(): Promise<ApiResponse<User>> {
    return this.request<User>('/auth/me');
  }

  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // If login successful, save token
    if (response.success && response.data?.token) {
      this.setAuthToken(response.data.token);
    }

    return response;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.request<void>('/auth/logout', {
      method: 'POST',
    });

    // Remove token regardless of response
    this.removeAuthToken();

    return response;
  }

  // Analytics endpoints
  async getAnalytics(): Promise<ApiResponse<Analytics>> {
    return this.request<Analytics>('/analytics');
  }

  // Settings endpoints
  async getSettings(): Promise<ApiResponse<Settings>> {
    return this.request<Settings>('/settings');
  }

  async updateSettings(settingsData: UpdateSettingsRequest): Promise<ApiResponse<Settings>> {
    return this.request<Settings>('/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // Notifications endpoints
  async getNotifications(): Promise<ApiResponse<Notification[]>> {
    return this.request<Notification[]>('/notifications');
  }

  async createNotification(notificationData: CreateNotificationRequest): Promise<ApiResponse<Notification>> {
    return this.request<Notification>('/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  async updateNotification(notificationId: string, notificationData: UpdateNotificationRequest): Promise<ApiResponse<Notification>> {
    return this.request<Notification>(`/notifications/${notificationId}`, {
      method: 'PUT',
      body: JSON.stringify(notificationData),
    });
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  // Clear cache method
  clearCache(): void {
    cache.clear();
  }

  // Clear specific cache entry
  clearCacheEntry(endpoint: string): void {
    const keys = Array.from(cache.keys());
    keys.forEach(key => {
      if (key.includes(endpoint)) {
        cache.delete(key);
      }
    });
  }
} 